import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus,
  PencilSimple,
  Trash,
  MagnifyingGlass,
  Export,
  FileCsv,
  FileXls,
  FilePdf,
  Lightning,
  Star,
  Bell,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
// Phase 992 — jsPDF/autoTable removed from Tasks: server-side PDF is used
// so Greek text renders correctly. See exportToPDF for the new flow.
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '-';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

export default function Tasks() {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const buildingFilterId = searchParams.get('building_id') || '';
  const [tasks, setTasks] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deletingTask, setDeletingTask] = useState(null);
  const [formData, setFormData] = useState({
    building_id: '',
    apartment_number: '',
    start_date: '',
    reminder_date: '',
    is_important: false,
    status: 'pending',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.building_id) {
      fetchApartments(formData.building_id);
    } else {
      setApartments([]);
    }
  }, [formData.building_id]);

  const fetchData = async () => {
    try {
      const [tasksRes, buildingsRes] = await Promise.all([
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
      ]);
      setTasks(tasksRes.data);
      setBuildings(buildingsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async (buildingId) => {
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/apartments`);
      setApartments(res.data);
    } catch {
      setApartments([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.reminder_date) payload.reminder_date = null;

      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.task_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/tasks`, payload, { withCredentials: true });
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      setEditingTask(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleQuickUpdate = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/quick-update`, {}, { withCredentials: true });
      toast.success(t('cleanedToday'));
      fetchData();
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/tasks/${deletingTask.task_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeleteDialogOpen(false);
      setDeletingTask(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const resetForm = () => {
    setFormData({
      building_id: '',
      apartment_number: '',
      start_date: '',
      reminder_date: '',
      is_important: false,
      status: 'pending',
      notes: ''
    });
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setFormData({
      building_id: task.building_id,
      apartment_number: task.apartment_number || '',
      start_date: task.start_date,
      reminder_date: task.reminder_date || '',
      is_important: task.is_important || false,
      status: task.status,
      notes: task.notes || ''
    });
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingTask(null);
    resetForm();
    setDialogOpen(true);
  };

  const getRowClass = (task) => {
    if (task.is_important) return 'bg-amber-50/60 border-l-4 border-l-amber-400';
    if (task.status === 'completed') return 'bg-emerald-50/40';
    const today = new Date().toISOString().split('T')[0];
    if (task.reminder_date && task.reminder_date < today && task.status !== 'completed') return 'bg-red-50/50';
    if (task.status === 'upcoming') return 'bg-orange-50/40';
    return '';
  };

  const getStatusBadge = (task) => {
    const today = new Date().toISOString().split('T')[0];
    const isOverdue = task.reminder_date && task.reminder_date < today && task.status !== 'completed';
    if (task.status === 'completed') return 'bg-emerald-50 text-emerald-700 border border-emerald-300';
    if (isOverdue) return 'bg-red-50 text-red-700 border border-red-300';
    if (task.status === 'upcoming') return 'bg-orange-50 text-orange-700 border border-orange-300';
    return 'bg-amber-50 text-amber-700 border border-amber-300';
  };

  const getStatusLabel = (task) => {
    const today = new Date().toISOString().split('T')[0];
    if (task.status === 'completed') return t('completed');
    if (task.reminder_date && task.reminder_date < today) return t('overdue');
    return t(task.status);
  };

  const getReminderBadge = (date) => {
    if (!date) return null;
    const today = new Date().toISOString().split('T')[0];
    const isOverdue = date < today;
    const daysLeft = Math.ceil((new Date(date) - new Date(today)) / (1000 * 60 * 60 * 24));
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border ${
        isOverdue ? 'bg-red-50 text-red-700 border-red-300' : daysLeft <= 3 ? 'bg-orange-50 text-orange-700 border-orange-300' : 'bg-blue-50 text-blue-700 border-blue-200'
      }`}>
        <Bell size={12} weight="fill" />
        {formatDateDMY(date)}
      </span>
    );
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.building_name?.toLowerCase().includes(search.toLowerCase()) ||
      task.apartment_number?.toLowerCase().includes(search.toLowerCase()) ||
      task.notes?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesBuilding = !buildingFilterId || task.building_id === buildingFilterId;
    return matchesSearch && matchesStatus && matchesBuilding;
  });

  const filteredBuilding = buildingFilterId
    ? buildings.find((b) => b.building_id === buildingFilterId)
    : null;

  const clearBuildingFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('building_id');
    setSearchParams(next);
  };

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (a.is_important !== b.is_important) return b.is_important ? 1 : -1;
    const statusOrder = { pending: 1, upcoming: 2, completed: 3 };
    const today = new Date().toISOString().split('T')[0];
    const aOverdue = a.reminder_date && a.reminder_date < today && a.status !== 'completed';
    const bOverdue = b.reminder_date && b.reminder_date < today && b.status !== 'completed';
    if (aOverdue !== bOverdue) return bOverdue ? -1 : 1;
    return (statusOrder[a.status] || 1) - (statusOrder[b.status] || 1);
  });

  const exportToCSV = () => {
    const data = sortedTasks.map(t => ({
      [language === 'el' ? 'Κτίριο' : 'Building']: t.building_name,
      [language === 'el' ? 'Διαμέρισμα' : 'Apartment']: t.apartment_number || '-',
      [language === 'el' ? 'Ημερομηνία' : 'Date']: t.start_date,
      [language === 'el' ? 'Υπενθύμιση' : 'Reminder']: t.reminder_date || '-',
      [language === 'el' ? 'Κατάσταση' : 'Status']: t.status,
      [language === 'el' ? 'Σημαντικό' : 'Important']: t.is_important ? '!' : '',
      [language === 'el' ? 'Σημειώσεις' : 'Notes']: t.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'reminders.csv');
  };

  const exportToExcel = () => {
    const data = sortedTasks.map(t => ({
      Building: t.building_name,
      Apartment: t.apartment_number || '-',
      Date: t.start_date,
      Reminder: t.reminder_date || '-',
      Status: t.status,
      Important: t.is_important ? 'Yes' : '',
      Notes: t.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reminders');
    XLSX.writeFile(wb, 'reminders.xlsx');
  };

  const exportToPDF = async () => {
    // Phase 992 — Server-side PDF (ReportLab + FreeSans) so Greek text
    // renders correctly. The old jsPDF client-side export used a font
    // subset that mangled Greek characters into boxes. We now send the
    // filtered task IDs to the backend which returns a Greek-safe,
    // landscape-A4 PDF with wrapping columns.
    try {
      const res = await axios.post(
        `${API}/export/reminders/pdf`,
        {
          task_ids: sortedTasks.map(x => x.task_id),
          title: language === 'el' ? 'Υπενθυμίσεις / Εργασίες' : 'Reminders / Tasks',
          language,
        },
        { responseType: 'blob', withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reminders.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('Reminders PDF export failed', err);
      toast.error(language === 'el' ? 'Αποτυχία εξαγωγής PDF' : 'PDF export failed');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">{t('loading')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="tasks-page">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                placeholder={t('search') + '...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-zinc-200 rounded-none"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px] rounded-none" data-testid="status-filter">
                  <SelectValue placeholder={t('status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="pending">{t('pending')}</SelectItem>
                  <SelectItem value="completed">{t('completed')}</SelectItem>
                  <SelectItem value="upcoming">{t('upcoming')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-zinc-500">{sortedTasks.length} {t('reminders')}</span>
            <div className="flex gap-2">
              {filteredBuilding && (
                <button
                  type="button"
                  onClick={clearBuildingFilter}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-800 text-xs hover:bg-blue-100 transition-colors"
                  data-testid="clear-building-filter"
                  title="Δείτε όλα τα tasks (όλων των κτιρίων)"
                >
                  <span className="font-semibold">Κτίριο:</span>
                  <span className="font-mono">{filteredBuilding.name}</span>
                  <span className="text-blue-500 ml-1">✕</span>
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-zinc-200 rounded-none" data-testid="export-btn">
                    <Export size={18} className="mr-2" />
                    {t('export')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportToCSV}><FileCsv size={18} className="mr-2" />CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToExcel}><FileXls size={18} className="mr-2" />Excel</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToPDF}><FilePdf size={18} className="mr-2" />PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={openAddDialog} className="bg-zinc-950 rounded-none" data-testid="add-task-btn">
                <Plus size={18} className="mr-2" />
                {t('addReminder')}
              </Button>
            </div>
          </div>
        </div>

        {/* Table - Desktop */}
        <div className="hidden lg:block bg-white border border-zinc-200 overflow-x-auto">
          {sortedTasks.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">{t('noReminders')}</div>
          ) : (
            <table className="excel-table" data-testid="tasks-table">
              <thead>
                <tr>
                  <th className="w-8"></th>
                  <th>{t('buildings')}</th>
                  <th>{t('scope')}</th>
                  <th>{t('date')}</th>
                  <th>{t('status')}</th>
                  <th>{t('reminderDate')}</th>
                  <th>{t('notes')}</th>
                  <th className="w-28">{t('edit')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((task) => (
                  <tr key={task.task_id} className={getRowClass(task)} data-testid={`task-row-${task.task_id}`}>
                    <td className="text-center">
                      {task.is_important && <Star size={16} weight="fill" className="text-amber-500 inline" />}
                    </td>
                    <td className="font-medium">{task.building_name}</td>
                    <td className="font-mono text-sm">
                      {task.type === 'apartment' ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200" data-testid={`task-scope-${task.task_id}`}>
                          {t('apartment')}{task.apartment_number ? ` #${task.apartment_number}` : ''}
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold px-2 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200" data-testid={`task-scope-${task.task_id}`}>
                          {t('building')}
                        </span>
                      )}
                    </td>
                    <td className="font-mono text-sm">{formatDateDMY(task.start_date)}</td>
                    <td>
                      <span className={`px-2 py-0.5 text-xs font-mono uppercase ${getStatusBadge(task)}`}>
                        {getStatusLabel(task)}
                      </span>
                    </td>
                    <td>{getReminderBadge(task.reminder_date)}</td>
                    <td className="text-sm text-zinc-500 max-w-[200px] truncate">{task.notes || '-'}</td>
                    <td>
                      <div className="flex gap-1">
                        {task.status !== 'completed' && (
                          <button
                            onClick={() => handleQuickUpdate(task.task_id)}
                            className="p-2 hover:bg-emerald-50 text-emerald-600 transition-colors"
                            title={t('markComplete')}
                            data-testid={`quick-update-${task.task_id}`}
                          >
                            <Lightning size={18} weight="fill" />
                          </button>
                        )}
                        <button
                          onClick={() => openEditDialog(task)}
                          className="p-2 hover:bg-zinc-100 transition-colors"
                          data-testid={`edit-task-${task.task_id}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => { setDeletingTask(task); setDeleteDialogOpen(true); }}
                            className="p-2 hover:bg-red-50 text-red-600 transition-colors"
                            data-testid={`delete-task-${task.task_id}`}
                          >
                            <Trash size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Cards - Mobile/Tablet */}
        <div className="lg:hidden space-y-3">
          {sortedTasks.length === 0 ? (
            <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">
              {t('noReminders')}
            </div>
          ) : (
            sortedTasks.map((task) => (
              <div key={task.task_id} className={`bg-white border border-zinc-200 p-4 ${task.is_important ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`task-card-${task.task_id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      {task.is_important && <Star size={14} weight="fill" className="text-amber-500" />}
                      <h3 className="font-medium text-sm">{task.building_name}</h3>
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {task.apartment_number && <span className="font-mono">#{task.apartment_number}</span>}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-mono uppercase ${getStatusBadge(task)}`}>
                    {getStatusLabel(task)}
                  </span>
                </div>
                {task.notes && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{task.notes}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
                  <span className="font-mono">{formatDateDMY(task.start_date)}</span>
                  {task.reminder_date && getReminderBadge(task.reminder_date)}
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-100">
                  {task.status !== 'completed' && (
                    <Button
                      onClick={() => handleQuickUpdate(task.task_id)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-none"
                      size="sm"
                      data-testid={`mobile-quick-update-${task.task_id}`}
                    >
                      <Lightning size={14} weight="fill" className="mr-1" />
                      {t('markComplete')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => openEditDialog(task)}
                    className="flex-1 rounded-none"
                    size="sm"
                  >
                    <PencilSimple size={14} className="mr-1" />
                    {t('edit')}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {editingTask ? t('editReminder') : t('addReminder')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Building */}
              <div>
                <Label>{t('buildings')}</Label>
                <Select
                  value={formData.building_id}
                  onValueChange={(val) => setFormData({ ...formData, building_id: val, apartment_number: '' })}
                  required
                >
                  <SelectTrigger className="mt-1 rounded-none" data-testid="building-select">
                    <SelectValue placeholder={t('buildings')} />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings.map(b => (
                      <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Apartment */}
              <div>
                <Label>{t('apartment')}</Label>
                <Select
                  value={formData.apartment_number || '__none__'}
                  onValueChange={(val) => setFormData({ ...formData, apartment_number: val === '__none__' ? '' : val })}
                >
                  <SelectTrigger className="mt-1 rounded-none" data-testid="apartment-select">
                    <SelectValue placeholder={t('wholeBuilding')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('wholeBuilding')}</SelectItem>
                    {apartments.map(a => (
                      <SelectItem key={a.apartment_id} value={a.apartment_number}>
                        {a.apartment_number}{a.owner_name ? ` — ${a.owner_name}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div>
                <Label>{t('status')}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(val) => setFormData({ ...formData, status: val })}
                >
                  <SelectTrigger className="mt-1 rounded-none" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="completed">{t('completed')}</SelectItem>
                    <SelectItem value="upcoming">{t('upcoming')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date + Reminder */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">{t('date')}</Label>
                  <DatePickerDMY
                    value={formData.start_date}
                    onChange={(v) => setFormData({ ...formData, start_date: v })}
                    className="mt-1 font-mono"
                    data-testid="start-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reminder_date" className="flex items-center gap-1.5">
                    <Bell size={14} className="text-blue-600" />
                    {t('reminderDate')}
                  </Label>
                  <DatePickerDMY
                    value={formData.reminder_date}
                    onChange={(v) => setFormData({ ...formData, reminder_date: v })}
                    className="mt-1 font-mono"
                    data-testid="reminder-date-input"
                  />
                </div>
              </div>

              {/* Important flag */}
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="important-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_important}
                    onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                    className="w-4 h-4 accent-amber-500"
                    data-testid="important-checkbox"
                  />
                  <Star size={16} weight="fill" className="text-amber-500" />
                  <span className="text-sm font-medium">{t('important')}</span>
                </label>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-1 rounded-none"
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-task-btn">
                  {t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteReminder')}</AlertDialogTitle>
              <AlertDialogDescription>{t('confirmDelete')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-btn">
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
